import {
  PlanTable,
  PricingTableCondensed
} from '@components/pricing/PricingTable';
import {
  platformPricing,
  authenticationAndUserFeatures,
  sessionManagementFeatures,
  administrationAndDashboardFeatures,
  b2bSaasFeatures,
  supportAndCompliance,
  securityFeatures
} from '@components/pricing/content';
import { DottedBorderSide } from '@components/ui/DottedBorder';
import { Badge } from '@components/ui/Badge';
import { motion } from 'framer-motion';
import { useState } from 'react';

const planTypes: {
  plan: 'freePlan' | 'proPlan' | 'proPlanPlus';
  title: string;
  additional?: string;
  description: string;
}[] = [
  {
    plan: 'freePlan',
    title: 'Free Plan',
    description: 'Everything you need to get started in minutes. For free.'
  },
  {
    plan: 'proPlan',
    title: 'Pro Plan',
    description: 'Advanced functionality to take your app to the next level.'
  },
  {
    plan: 'proPlanPlus',
    title: 'Pro with ',
    additional: 'Add-ons',
    description: 'Choose only the add-ons you need'
  }
];

export function PricingSection() {
  const [plan, setPlan] = useState<'freePlan' | 'proPlan' | 'proPlanPlus'>(
    'freePlan'
  );

  return (
    <div>
      <div className="sticky top-20 z-10 grid grid-cols-3 bg-white sm:px-6 md:px-8 lg:hidden">
        {planTypes.map((planType, index) => (
          <button
            key={index}
            onClick={() => setPlan(planType.plan)}
            className="relative border-b border-newGray-200 p-4 text-center text-[0.8125rem]"
          >
            {index < 2 && <DottedBorderSide side="right" overhang={0} />}
            <h4 className="flex flex-wrap items-center justify-center gap-1 font-semibold leading-6 text-primary">
              <span>{planType.title}</span>
              {planType.additional && (
                <>
                  <Badge label={planType.additional} intent="purple" />
                </>
              )}
            </h4>
            <p className="hidden leading-5 text-secondary sm:block">
              {planType.description}
            </p>
            {plan === planType.plan && (
              <motion.div
                className="absolute inset-x-0 -bottom-px z-10 h-px bg-newGray-950"
                layoutId="underline"
              />
            )}
          </button>
        ))}
      </div>

      <div className="space-y-10">
        <PricingTableCondensed
          data={platformPricing}
          title="Platform pricing"
          planType={plan}
        />

        <PricingTableCondensed
          data={authenticationAndUserFeatures}
          title="Authentication & user features"
          planType={plan}
        />

        <PricingTableCondensed
          data={securityFeatures}
          title="Security features"
          planType={plan}
        />

        <PricingTableCondensed
          data={sessionManagementFeatures}
          title="Session management features"
          planType={plan}
        />

        <PricingTableCondensed
          data={administrationAndDashboardFeatures}
          title="Administration & Dashboard features"
          planType={plan}
        />

        <PricingTableCondensed
          data={b2bSaasFeatures}
          title="B2B SaaS features"
          planType={plan}
        />

        <PricingTableCondensed
          data={supportAndCompliance}
          title="Support & Compliance"
          planType={plan}
        />
      </div>

      <div className="hidden space-y-20 lg:block">
        <PlanTable data={platformPricing} title="Platform pricing" />

        <PlanTable
          data={authenticationAndUserFeatures}
          title="Authentication & user features"
        />

        <PlanTable data={securityFeatures} title="Security features" />

        <PlanTable
          data={sessionManagementFeatures}
          title="Session management features"
        />

        <PlanTable
          data={administrationAndDashboardFeatures}
          title="Administration & Dashboard features"
          customTableHeader="Pro with Administration add-on"
        />

        <PlanTable
          data={b2bSaasFeatures}
          title="B2B SaaS features"
          customTableHeader="Pro with B2B SaaS add-on"
        />

        <PlanTable data={supportAndCompliance} title="Support & Compliance" />
      </div>
    </div>
  );
}
